/**
 * Decode a base64 data URL (canvas.toDataURL output) straight into a Blob.
 * Deliberately NOT fetch(dataUrl): the strict CSP's connect-src 'self' blocks
 * fetching data: URLs, which silently broke the signature Accept button.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const mime = /data:([^;]+)/.exec(meta)?.[1] ?? 'application/octet-stream';
  const raw = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Downscale a captured image blob to a max dimension and re-encode as JPEG.
 * Keeps uploads small for slow cellular links. Falls back to the original
 * blob if the browser can't decode/encode it.
 */
export async function resizeImage(blob: Blob, max = 1600, quality = 0.85): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const ratio = Math.min(1, max / Math.max(img.width, img.height));
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(img, 0, 0, w, h);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
    );
    return out ?? blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}
